import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import backIcon from '@/assets/paytm/back.png'
import driverIcon from '@/assets/paytm/driver.png'
import phoneIcon from '@/assets/paytm/phone.png'
import dropIcon from "@/assets/paytm/map_pin.png"
import rtoIcon from "@/assets/paytm/rto.png"
import pickupIcon from "@/assets/paytm/pickupLoc.png"
import type { ReactNode } from 'react'
import dayjs from 'dayjs'
import { Api, formatStepDuration, getLogo, getLspName, isOrderCancellable, isOrderReallocatable, orderStatus } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Fulfillment, Order } from '@/lib/interfaces'
import { AlertTriangle, Ban, CheckCircle2, CircleX, Copy, Info, Loader2, RefreshCw, Undo, UserSearch, UserX } from 'lucide-react'
import { toast } from 'sonner'
import Tooltip from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { BlockRiderDialog } from '@/components/features/orders/block-rider'
import { RaiseIssueDialog } from '@/components/features/orders/raise-issue'
import { CancelOrderDialog } from '@/components/features/orders/confirm-cancel'
import { ReAllocateOrderDialog } from '@/components/features/orders/order-reallocate'
import { RiderAssignConfirm } from '@/components/features/orders/confirm-rider-assign'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'

export const Route = createFileRoute('/shikhar/orders/$orderId')({
    component: RouteComponent,
})

const TopHeader = ({ orderId, createdAt, orderState, onBackClick }: { orderId: string, createdAt: string, orderState: string, onBackClick: () => void }) => {
    return <div className='bg-transparent px-4 py-5 flex items-center justify-between'>
        <div className='flex items-start'>
            <img src={backIcon} className='w-6 mr-5 mt-1 cursor-pointer' onClick={onBackClick} />
            <div>
                <p className='text-lg font-bold'>{orderId}</p>
                <p className='text-sm font-medium text-gray-700'>{`Created at : ${dayjs(createdAt).format('MMM D, hh:mm:ss A')}`}</p>
            </div>
        </div>
        <div className='px-6 py-2 rounded-3xl bg-black'>
            <p className='text-white font-semibold'>{orderState || '-'}</p>
        </div>
    </div>
}


const StatusDisplay = ({ orderState, riderName, deliveredAt }: { orderState: string, riderName: string, deliveredAt?: string }) => {
    let statusTxt = ''
    let statusSubTxt = ''
    let statusIcon: ReactNode = <div className='w-12 h-12 rounded-full bg-gray-300 mr-4 shrink-0' />

    if (orderStatus[orderState] < 4) {
        statusTxt = 'Searching for rider'
        statusSubTxt = 'Rider will be assigned shortly.'
    } else if (orderStatus[orderState] === 4) {
        statusTxt = 'Rider assigned'
        statusSubTxt = 'Your delivery partner.'
    } else if (orderStatus[orderState] > 4 && orderStatus[orderState] < 9) {
        statusSubTxt = `${riderName} is on the way with your order`
        if (orderState === 'At-pickup') {
            statusTxt = 'Arrived at pickup location'
        } else if (orderState === 'At-delivery') {
            statusTxt = 'Reached drop location'
        } else {
            statusTxt = 'Order out for delivery'
        }
    } else if (orderState === 'Order-delivered') {
        statusTxt = 'WooHoo! Your order is Delivered 🎉🎉'
        statusSubTxt = `Delivered at ${dayjs(deliveredAt).format('hh:mm A')}`
        statusIcon = (
            <div className='w-12 h-12 rounded-full bg-green-100 text-green-600 mr-4 shrink-0 flex items-center justify-center'>
                <CheckCircle2 className='w-6 h-6' />
            </div>
        )
    } else {
        if (orderState === 'Cancelled') {
            statusTxt = 'Cancelled'
        } else {
            statusTxt = 'Order  Returned !'
        }
        statusSubTxt = 'Refund for this order will be processed within 24 hours'
    }

    return <div className='px-4 py-3 flex border bg-white rounded-2xl items-center w-full'>
        {orderStatus[orderState] > 10 ? <img src={rtoIcon} className='w-12 mr-4 shrink-0' /> : statusIcon}
        <div className='w-full1 overflow-hidden'>
            <p className='text-sm font-semibold mb-1'>{statusTxt}</p>
            <div>
                <p className='text-xs font-medium text-gray-400'>{statusSubTxt}</p>
            </div>
        </div>
    </div>
}

const RiderContact = ({ riderName, riderPhone }: { riderName: string, riderPhone: string }) => {
    return <div className='px-4 py-3 flex border bg-white rounded-2xl items-center justify-between'>
        <div className='flex items-center'>
            <img src={driverIcon} className='w-12 mr-4' />
            <div>
                <p className='text-sm font-semibold mb-1'>{riderName}</p>
                <p className='text-xs font-semibold text-gray-400'>Your delivery partner</p>
            </div>
        </div>
        <a href={`tel:${riderPhone}`}><img src={phoneIcon} className='w-6' /></a>
    </div>
}

const Otp = ({ dropOtp, rtoOtp }: { dropOtp: string, rtoOtp: string }) => {
    return <div className='flex items-center w-full justify-between'>
        <div className='px-3 py-2.5 border mr-8 rounded-xl flex-1'>
            <div className='flex items-center gap-1 text-sm text-gray-400 font-medium'>
                <span>Drop OTP</span>
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
                            aria-label="Drop OTP info"
                        >
                            <Info size={16} />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent side="top" align="start" className="w-72 text-sm">
                        Share this OTP with the rider during delivery, It will also be shared with Receiver once order is confirmed.
                    </PopoverContent>
                </Popover>
            </div>
            <p className='text-lg text-dark-midnight font-bold'>{dropOtp || '- -'}</p>
        </div>
        <div className='px-3 py-2.5 border rounded-xl flex-1'>
            <div className='flex items-center gap-1 text-sm text-gray-400 font-medium'>
                <span>RTO OTP</span>
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
                            aria-label="RTO OTP info"
                        >
                            <Info size={16} />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent side="top" align="start" className="w-72 text-sm">
                        Use this OTP if the order is returned and needs RTO verification.
                    </PopoverContent>
                </Popover>
            </div>
            <p className='text-lg text-dark-midnight font-bold'>{rtoOtp || '- -'}</p>
        </div>
    </div>
}

const OrderDetails = ({ order }: { order: Order }) => {
    return <div className='border rounded-2xl overflow-hidden bg-white'>
        <div className='bg-gray-100 px-6 py-4 flex items-center justify-between gap-3'>
            <div>
                <p className='text-lg font-semibold'>Order Details</p>
                <p className='mt-1 text-sm font-semibold text-gray-700'>{order.orderState || '-'}</p>
            </div>
            <button
                type="button"
                className='p-1.5 rounded-md border border-gray-300 hover:bg-gray-200'
                onClick={() => {
                    const text = [
                        `orderId : ${order.orderId || '-'}`,
                        `clientOrderId : ${order.clientOrderId || '-'}`,
                        `networkOrderId : ${order.networkOrderId || '-'}`,
                        `sellerOrderId : ${order.sellerOrderId || 'null'}`,
                        `orderState : ${order.orderState || '-'}`,
                        `providerId : ${order.providerId || order.pcc || order.bppId || '-'}`,
                        `riderName : ${order.riderName || '-'}`,
                        `riderNumber : ${order.riderNumber || '-'}`,
                        `trackingUrl : ${order.trackingUrl || '-'}`,
                        `awbNumber : ${order.awbNumber || order.networkOrderId || '-'}`,
                        `assignedAt : ${order.assignedAt ? dayjs(order.assignedAt).format('hh:mm') : '-'}`
                    ].join('\n')
                    navigator.clipboard.writeText(text)
                    toast.success('Order details copied to clipboard')
                }}
                aria-label="Copy order id"
            >
                <Copy className='w-4 h-4' />
            </button>
        </div>
        <div className='px-6 py-5'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                <div>
                    <p className='text-xs text-gray-500'>Order Id</p>
                    <p className='text-sm font-semibold'>{order.orderId || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Client Order Id</p>
                    <p className='text-sm font-semibold'>{order.clientOrderId || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Network Order Id</p>
                    <p className='text-sm font-semibold'>{order.networkOrderId || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Seller Order Id</p>
                    <p className='text-sm font-semibold'>-</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>AWB Number</p>
                    <p className='text-sm font-semibold'>{order.networkOrderId || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Selection Mode</p>
                    <p className='text-sm font-semibold'>{order.selectionMode || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>LSP</p>
                    <p className='text-sm font-semibold'>{getLspName(order.bppId, order.providerId) || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Category</p>
                    <p className='text-sm font-semibold'>{order.orderCategory || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>State</p>
                    <p className='text-sm font-semibold'>{order.orderState || '-'}</p>
                </div>
                <div>
                    <p className='text-xs text-gray-500'>Distance</p>
                    <p className='text-sm font-semibold'>{order.distance ? `${order.distance} km` : '0 km'}</p>
                </div>
            </div>
            <div className='mt-6'>
                <button
                    type="button"
                    className='inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-process-cyan text-white font-semibold'
                    onClick={() => window.location.reload()}
                >
                    <RefreshCw className='w-4 h-4' />
                    Refresh
                </button>
            </div>
        </div>
    </div>
}

const ActionButton = ({
    label,
    icon,
    disabled,
    variant = 'red',
    ...props
}: {
    label: string
    icon: ReactNode
    disabled?: boolean
    variant?: 'red' | 'blue'
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const colorClasses = variant === 'blue'
        ? 'border-blue-500 text-blue-500'
        : 'border-red-400 text-red-500'
    return (
        <button
            type="button"
            disabled={disabled}
            className={`w-full flex items-center justify-center gap-3 border rounded-2xl py-3 px-5 active:opacity-70 ${colorClasses} ${disabled ? 'opacity-40' : 'cursor-pointer'}`}
            {...props}
        >
            {icon}
            <p className='font-semibold'>{label}</p>
        </button>
    )
}

const Fulfillments = ({ fulfillments, order }: { fulfillments: Fulfillment[], order: Order }) => {
    return <div className='border rounded-2xl overflow-hidden bg-white'>
        <div className='bg-gray-100 px-6 py-4'>
            <p className='text-lg font-semibold'>Fulfillments</p>
        </div>
        <div className='px-6 py-5'>
            {fulfillments.length ? (
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='text-gray-500'>
                            <tr>
                                <th className='text-left py-2'>LSP</th>
                                <th className='text-left py-2'>Created At</th>
                                <th className='text-left py-2'>Status</th>
                                <th className='text-left py-2'>Rider</th>
                                <th className='text-left py-2'>Assigned At</th>
                                <th className='text-left py-2'>Network Order Id</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fulfillments.map((fulfillment) => (
                                <tr key={fulfillment.networkOrderId} className='border-t'>
                                    <td className='py-3 text-gray-900'>
                                        <Tooltip title={getLspName(fulfillment.bppId) || fulfillment.bppId || '-'}>
                                            <div className='w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center'>
                                                <img src={getLogo(fulfillment.bppId)} className='w-6 h-6 object-contain' alt={fulfillment.bppId || 'LSP'} />
                                            </div>
                                        </Tooltip>
                                    </td>
                                    <td className='py-3 text-gray-900'>{fulfillment.createdAt ? dayjs(fulfillment.createdAt).format('hh:mm A') : '-'}</td>
                                    <td className='py-3 text-gray-900'>{fulfillment.fulfillmentState || '-'}</td>
                                    <td className='py-3 text-gray-900'>
                                        <div>{fulfillment.riderName || '-'}</div>
                                        <div className='text-xs text-gray-500 flex items-center gap-2'>
                                            {fulfillment.riderNumber || '-'}
                                            <BlockRiderDialog
                                                riderName={fulfillment.riderName || ''}
                                                riderPhone={fulfillment.riderNumber || ''}
                                                bppId={fulfillment.bppId || ''}
                                                lsp={getLspName(fulfillment.bppId) || fulfillment.bppId || ''}
                                                disabled={!fulfillment.riderNumber || !fulfillment.riderName}
                                            >
                                                <span className='inline-flex items-center justify-center w-7 h-7 rounded-full border border-red-200 text-red-500 cursor-pointer'>
                                                    <UserX className='w-4 h-4' />
                                                </span>
                                            </BlockRiderDialog>
                                        </div>
                                    </td>
                                    <td className='py-3 text-gray-900'>{fulfillment.assignedAt ? dayjs(fulfillment.assignedAt).format('hh:mm A') : '-'}</td>
                                    <td className='py-3 text-gray-900 break-all'>
                                        <div className='flex items-center justify-between gap-2'>
                                            <span>{fulfillment.networkOrderId || '-'}</span>
                                            <RaiseIssueDialog
                                                orderId={order.orderId}
                                                orderState={order.orderState || ''}
                                                networkOrderId={order.networkOrderId || ''}
                                                orderAmount={order.orderAmount || 0}
                                                deliveryFee={order.deliveryFee || 0}
                                                disabled={!order.orderState || !order.networkOrderId}
                                            >
                                                <span className='inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 text-gray-500 cursor-pointer'>
                                                    <AlertTriangle className='w-4 h-4' />
                                                </span>
                                            </RaiseIssueDialog>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className='text-sm text-gray-500'>No fulfillments available.</p>
            )}
        </div>
    </div>
}

const PaymentDetails = ({ order }: { order: Order }) => {
    return <div className='border rounded-2xl overflow-hidden bg-white'>
        <div className='bg-gray-100 px-6 py-5 flex items-start justify-between'>
            <div>
                <p className='text-lg font-semibold'>Payment details</p>
                <p className='text-sm text-gray-600'>{`Order Id: ${order.clientOrderId}`}</p>
            </div>
            <div className='flex items-center gap-3'>
                <div className='text-right text-sm text-gray-600'>
                    <p>{dayjs(order.createdAt).format('D MMM YYYY')}</p>
                    <p>{dayjs(order.createdAt).format('hh:mm A')}</p>
                </div>
                <button
                    type="button"
                    className='p-2 rounded-md border border-gray-300 hover:bg-gray-200'
                    onClick={() => {
                        const text = [
                            `Order Id: ${order.clientOrderId || '-'}`,
                            `Date: ${dayjs(order.createdAt).format('D MMM YYYY')}`,
                            `Time: ${dayjs(order.createdAt).format('hh:mm A')}`,
                            '',
                            'Pickup:',
                            order.pickupAddress?.name || '-',
                            order.pickupPhone || '-',
                            order.pickupAddress?.line1 || '-',
                            order.pickupAddress?.line2 || '-',
                            `${order.pickupAddress?.city || '-'}, ${order.pickupAddress?.state || '-'} - ${order.pickupPincode || '-'}`,
                            '',
                            'Drop:',
                            order.dropAddress?.name || '-',
                            order.dropPhone || '-',
                            order.dropAddress?.line1 || '-',
                            order.dropAddress?.line2 || '-',
                            `${order.dropAddress?.city || '-'}, ${order.dropAddress?.state || '-'} - ${order.dropPincode || '-'}`,
                            '',
                            `Order Amount: \u20B9 ${order.orderAmount.toFixed(2)}`,
                            `Delivery Fee: \u20B9 ${order.deliveryFee.toFixed(2)}`,
                            `Platform Fee: \u20B9 ${order.platformFee.toFixed(2)}`,
                            `Total Fee With GST: \u20B9 ${order.priceWithGST.toFixed(2)}`
                        ].join('\n')
                        navigator.clipboard.writeText(text)
                        toast.success('Payment details copied to clipboard')
                    }}
                    aria-label="Copy order id"
                >
                    <Copy className='w-4 h-4' />
                </button>
            </div>
        </div>
        <div className='px-6 py-5'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                    <p className='text-sm text-gray-500'>Pickup</p>
                    <p className='text-sm font-semibold'>{order.pickupAddress.name}</p>
                    <p className='text-sm text-gray-600'>{order.pickupPhone}</p>
                    <p className='text-sm text-gray-600'>{order.pickupAddress.line1}</p>
                    <p className='text-sm text-gray-600'>{order.pickupAddress.line2}</p>
                    <p className='text-sm text-gray-600'>{`${order.pickupAddress.city}, ${order.pickupAddress.state} - ${order.pickupPincode}`}</p>
                </div>
                <div>
                    <p className='text-sm text-gray-500'>Drop</p>
                    <p className='text-sm font-semibold'>{order.dropAddress.name}</p>
                    <p className='text-sm text-gray-600'>{order.dropPhone}</p>
                    <p className='text-sm text-gray-600'>{order.dropAddress.line1}</p>
                    <p className='text-sm text-gray-600'>{order.dropAddress.line2}</p>
                    <p className='text-sm text-gray-600'>{`${order.dropAddress.city}, ${order.dropAddress.state} - ${order.dropPincode}`}</p>
                </div>
            </div>
            <div className='mt-6 border-t pt-4'>
                <div className='flex items-center justify-between py-1'>
                    <p className='text-sm text-gray-700'>Order Amount</p>
                    <p className='text-sm font-semibold'>{`\u20B9 ${order.orderAmount.toFixed(2)}`}</p>
                </div>
                <div className='flex items-center justify-between py-1'>
                    <p className='text-sm text-gray-700'>Delivery Fee</p>
                    <p className='text-sm font-semibold'>{`\u20B9 ${order.deliveryFee.toFixed(2)}`}</p>
                </div>
                <div className='flex items-center justify-between py-1'>
                    <p className='text-sm text-gray-700'>Platform Fee</p>
                    <p className='text-sm font-semibold'>{`\u20B9 ${order.platformFee.toFixed(2)}`}</p>
                </div>
                <div className='flex items-center justify-between py-1 border-t mt-2 pt-3'>
                    <p className='text-sm font-semibold'>Total Fee With GST</p>
                    <p className='text-sm font-semibold'>{`\u20B9 ${order.priceWithGST.toFixed(2)}`}</p>
                </div>
            </div>
        </div>
    </div>
}

const LocationDetails = ({ pickupAddr, dropAddr }: {
    pickupAddr: string,
    dropAddr: string,
}) => {
    return <div className='w-full border rounded-xl pl-9 py-4 pr-4 relative'>
        <div className='w-full mb-6'>
            <p className='text-xs font-medium mb-1'>Pickup Details</p>
            <textarea className='resize-none outline-none border-none w-full text-sm font-semibold' value={pickupAddr} readOnly />
        </div>
        <div>
            <p className='text-xs font-medium mb-1'>Drop Details</p>
            <textarea className='resize-none outline-none border-none w-full text-sm font-semibold' value={dropAddr} readOnly />
        </div>
        <div className="absolute h-20 left-4 top-6 border-l-2 border-dotted border-gray-300 w-10">
            <img src={pickupIcon} className="w-5 absolute -left-2.5 -top-3" />
            <img src={dropIcon} className="w-5 absolute -left-2.5 -bottom-5" />
        </div>
    </div>
}


const Attachments = ({ images }: { images: string[] }) => {
    return <div className='py-4 px-3 border rounded-xl'>
        <p className='font-bold mb-2'>Attachments</p>
        <div className='flex items-center'>
            {images.filter(e => e).map(e => {
                return <img src={e} className='w-20 mr-4' key={e} />
            })}
        </div>
    </div>
}


const Timeline = ({ order }: { order: Order }) => {
    const items = [
        { label: 'Order Created', time: order.createdAt },
        { label: 'Order Ready', time: order.orderReadyAt },
        { label: 'Agent Assigned', time: order.assignedAt },
        { label: 'At Pickup', time: order.atpickupAt },
        { label: 'Order Picked Up', time: order.pickedupAt },
        { label: 'At Delivery', time: order.atdeliveryAt },
        { label: 'Order Delivered', time: order.deliveredAt },
        { label: 'Cancelled', time: order.cancelledAt },
        { label: 'RTO Picked Up', time: order.rtoPickedupAt },
        { label: 'RTO Delivered', time: order.rtoDeliveredAt },
    ]
        .filter(item => item.time)
        .sort((a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf())

    return <div className='border rounded-2xl overflow-hidden bg-white'>
        <div className='bg-gray-100 px-6 py-4'>
            <p className='text-lg font-semibold'>Timeline</p>
        </div>
        <div className='px-6 py-5'>
            {items.length ? (
                <div className='space-y-6'>
                    {items.map((item, index) => (
                        <div className='flex items-start gap-4' key={`${item.label}-${item.time}`}>
                            <div className='flex flex-col items-center mt-1'>
                                <span className='w-3 h-3 rounded-full border-2 border-process-cyan bg-white' />
                                {index < items.length - 1 ? <span className='w-px h-10 bg-gray-200 mt-2' /> : null}
                            </div>
                            <div>
                                <div className='flex items-center gap-2'>
                                    <p className='text-sm text-gray-600'>{dayjs(item.time).format('MMM D, hh:mm A')}</p>
                                    {index > 0 ? <Tooltip title='Time since previous step'>
                                        <span className='text-xs font-semibold text-process-cyan bg-process-cyan/10 rounded-full px-2 py-0.5 whitespace-nowrap'>+{formatStepDuration(dayjs(item.time).diff(dayjs(items[index - 1].time)))}</span>
                                    </Tooltip> : null}
                                </div>
                                <p className='text-sm font-semibold'>{item.label}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className='text-sm text-gray-500'>No timeline events available.</p>
            )}
        </div>
    </div>
}


function RouteComponent() {
    const navigate = useNavigate()
    const { orderId } = Route.useParams()
    const { token, role, unfulfilEnabled, accountId } = useStore(useShallow(state => ({
        token: state.token,
        role: state.selectedAccount.role,
        unfulfilEnabled: state.selectedAccount.unfulfil_enabled,
        accountId: state.selectedAccount.id
    })))
    const getOrderInfo = useQuery({
        queryKey: ['orderInfo', orderId],
        queryFn: () => Api<{ status: number, order: Order }>('/webui/order/info', "POST", { order: { id: orderId } }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false
    })

    const order = getOrderInfo.data?.order
    const isSuperAdmin = /super_admin/.test(role)

    return <div className="fixed top-0 bottom-0 left-0 right-0 bg-white z-50">
        {order ? <>
            <TopHeader orderId={order.clientOrderId} createdAt={order.createdAt} orderState={order.orderState} onBackClick={() => navigate({ to: '/shikhar/orders' })} />
            <div className='mt-4 h-[calc(100%-72px)] px-4 pb-4 *:my-4 overflow-auto order'>
                {order.orderState === 'Order-delivered' ? (
                    <StatusDisplay orderState={order.orderState} riderName={order.riderName} deliveredAt={order.deliveredAt} />
                ) : null}
                <div className='grid grid-cols-2 gap-4'>
                    {order.issueid ? (
                        <Link to="/shikhar/issues/$issueId" params={{ issueId: order.issueid }} className="block w-full">
                            <ActionButton label="Go to Issue" icon={<AlertTriangle className='text-blue-500' />} variant="blue" />
                        </Link>
                    ) : (
                        <RaiseIssueDialog
                            orderId={order.orderId}
                            orderState={order.orderState || ''}
                            networkOrderId={order.networkOrderId || ''}
                            orderAmount={order.orderAmount || 0}
                            deliveryFee={order.deliveryFee || 0}
                            disabled={!order.orderState || !order.networkOrderId}
                        >
                            <ActionButton label="Raise Issue" icon={<AlertTriangle className='text-red-500' />} disabled={!order.orderState || !order.networkOrderId} />
                        </RaiseIssueDialog>
                    )}
                    {(/unfulfilled/i.test(order.orderState || '')) ? (
                        <RiderAssignConfirm orderId={order.orderId} pickupOtp={order.pickupOtp || order.dropOtp || ''}>
                            <ActionButton label="Assign Rider" icon={<UserSearch className='text-blue-500' />} variant="blue" />
                        </RiderAssignConfirm>
                    ) : (
                        <ReAllocateOrderDialog
                            orderId={order.orderId}
                            accountId={accountId}
                            disabled={!isOrderReallocatable(order.orderState || '', unfulfilEnabled, accountId, isSuperAdmin)}
                            onSuccess={() => {}}
                        >
                            <ActionButton label="Re-Allocate" icon={<Undo className='text-red-500' />} disabled={!isOrderReallocatable(order.orderState || '', unfulfilEnabled, accountId, isSuperAdmin)} />
                        </ReAllocateOrderDialog>
                    )}
                    <CancelOrderDialog
                        orderId={order.orderId}
                        isSuperAdmin={isSuperAdmin}
                        orderState={order.orderState || ''}
                        disabled={!isOrderCancellable(order.orderState || '', isSuperAdmin)}
                    >
                        <ActionButton label="Cancel" icon={<CircleX className='text-red-500' />} disabled={!isOrderCancellable(order.orderState || '', isSuperAdmin)} />
                    </CancelOrderDialog>
                    <BlockRiderDialog
                        riderName={order.riderName || ''}
                        riderPhone={order.riderNumber || ''}
                        bppId={order.bppId || ''}
                        lsp={order.providerId || ''}
                        disabled={!isSuperAdmin || !order.riderNumber || !order.riderName}
                    >
                        <ActionButton label="Block Rider" icon={<Ban className='text-red-500' />} disabled={!isSuperAdmin || !order.riderNumber || !order.riderName} />
                    </BlockRiderDialog>
                </div>
                <OrderDetails order={order} />
                {order.riderName ? <RiderContact riderName={order.riderName} riderPhone={order.riderNumber} /> : null}
                {orderStatus[order.orderState] >= 9 ? (
                    <LocationDetails
                        pickupAddr={order.pickupAddress.name + ', ' + order.pickupAddress.line1 + ', ' + order.pickupAddress.line2}
                        dropAddr={order.dropAddress.name + ', ' + order.dropAddress.line1 + ', ' + order.dropAddress.line2}
                    />
                ) : (
                    <Otp dropOtp={order.dropOtp} rtoOtp={order.rtoOtp} />
                )}
                {order.pickupProof || order.deliveryProof ? <Attachments images={[order.pickupProof, order.deliveryProof]} /> : null}
                {orderStatus[order.orderState] >= 2 ? <PaymentDetails order={order} /> : null}
                <Timeline order={order} />
                <Fulfillments fulfillments={order.fulfillmentList || []} order={order} />
            </div>
        </> : <Loader2 className='animate-spin' />}
    </div>
}
